import { dayIndex, formatMinutesSigned, type AnalemmaPoint } from "@/lib/solar";
import { MUTED, SANS, SERIF, sx, sy } from "@/components/analemma/scale";

export function curvePath(points: AnalemmaPoint[]): string {
  return points.map((p, i) => `${i ? "L" : "M"}${sx(p.eot).toFixed(1)},${sy(p.dec).toFixed(1)}`).join(" ") + " Z";
}

export function MonthMarkers({ points, year }: { points: AnalemmaPoint[]; year: number }) {
  return (
    <>
      {Array.from({ length: 12 }, (_, m) => {
        const first = new Date(year, m, 1);
        const p = points[dayIndex(first)];
        return (
          <g key={`m${m}`}>
            <circle cx={sx(p.eot)} cy={sy(p.dec)} r="2" fill={MUTED} />
            <text x={sx(p.eot) + 6} y={sy(p.dec) + 3} fontSize="9" fontFamily={SERIF} fill={MUTED} fontStyle="italic">
              {first.toLocaleDateString(undefined, { month: "short" })}
            </text>
          </g>
        );
      })}
    </>
  );
}

// Today's dot sits on the curve; the label shows live EoT and declination.
export function TodayMarker({ point, eot, dec }: { point: AnalemmaPoint; eot: number; dec: number }) {
  const cx = sx(point.eot);
  const cy = sy(point.dec);
  const flip = point.eot > 5; // keep the label inside the plot near the right edge
  const lx = cx + (flip ? -14 : 14);
  return (
    <g>
      <circle cx={cx} cy={cy} r="12" fill="#c0501b" opacity="0.15" />
      <circle cx={cx} cy={cy} r="5" fill="#c0501b" stroke="#f6efe3" strokeWidth="1.5" />
      <text x={lx} y={cy - 14} textAnchor={flip ? "end" : "start"} fontFamily={SERIF} fill="#2b1f14" data-testid="solar-analemma-today-label">
        <tspan fontSize="11" fontStyle="italic">today</tspan>
        <tspan x={lx} dy="13" fontSize="10" fontFamily={SANS} fill="#5e4a35">
          {formatMinutesSigned(eot)} · {dec >= 0 ? "+" : "−"}
          {Math.abs(dec).toFixed(1)}°
        </tspan>
      </text>
    </g>
  );
}

export function SyncMarker({ dec }: { dec: number }) {
  return (
    <g>
      <circle cx={sx(0)} cy={sy(dec)} r="5" fill="#f6efe3" stroke="#2b1f14" strokeWidth="1.5" />
      <text x={sx(0)} y={sy(dec) - 10} textAnchor="middle" fontSize="10" fontFamily={SERIF} fontStyle="italic" fill="#2b1f14">
        next sync
      </text>
    </g>
  );
}
